import { KeyboardEvent, PointerEvent, useRef } from 'react';
import { clampFraction } from './clampFraction';
import classes from './Waveform.module.css';

const PLAYED_COLOUR = 'var(--mantine-color-gray-9)';
const IDLE_COLOUR = 'var(--mantine-color-gray-3)';

const KEY_STEP = 0.02;
const KEY_PAGE_STEP = 0.1;

const KEY_MOVES = new Map<string, (position: number) => number>([
  ['ArrowLeft', position => position - KEY_STEP],
  ['ArrowDown', position => position - KEY_STEP],
  ['ArrowRight', position => position + KEY_STEP],
  ['ArrowUp', position => position + KEY_STEP],
  ['PageDown', position => position - KEY_PAGE_STEP],
  ['PageUp', position => position + KEY_PAGE_STEP],
  ['Home', () => 0],
  ['End', () => 1],
]);

type WaveformProps = {
  bars: number[];
  /** Playback position within the segment, 0..1. Zero renders the whole waveform idle. */
  progress: number;
  onSeek: (fraction: number) => void;
  positionLabel: string;
};

export function Waveform({ bars, progress, onSeek, positionLabel }: WaveformProps) {
  const clamped = clampFraction(progress);
  const playedCount = Math.floor(clamped * bars.length);
  const trackRef = useRef<HTMLDivElement>(null);
  const draggingPointerRef = useRef<number | null>(null);

  const seekToPointer = (event: PointerEvent<HTMLDivElement>) => {
    const track = trackRef.current;
    if (!track) return;
    const { left, width } = track.getBoundingClientRect();
    if (width <= 0) return;
    onSeek(clampFraction((event.clientX - left) / width));
  };

  const startDrag = (event: PointerEvent<HTMLDivElement>) => {
    // A secondary button or a second finger must not grab the playhead mid-drag.
    const isDragStart = event.isPrimary && event.button === 0;
    if (!isDragStart || draggingPointerRef.current !== null) return;
    draggingPointerRef.current = event.pointerId;
    event.currentTarget.setPointerCapture(event.pointerId);
    seekToPointer(event);
  };

  const endDrag = (event: PointerEvent<HTMLDivElement>) => {
    if (draggingPointerRef.current !== event.pointerId) return;
    draggingPointerRef.current = null;
    event.currentTarget.releasePointerCapture(event.pointerId);
  };

  const continueDrag = (event: PointerEvent<HTMLDivElement>) => {
    if (draggingPointerRef.current !== event.pointerId) return;
    // A button released where we never saw the event (a context menu, say) ends the drag.
    if (event.buttons === 0) endDrag(event);
    else seekToPointer(event);
  };

  const seekOnKey = (event: KeyboardEvent<HTMLDivElement>) => {
    const move = KEY_MOVES.get(event.key);
    if (!move) return;
    event.preventDefault();
    onSeek(clampFraction(move(clamped)));
  };

  return (
    <div
      ref={trackRef}
      className={`${classes.wave} ${classes.seekable}`}
      role="slider"
      tabIndex={0}
      aria-label="Playback position"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(clamped * 100)}
      aria-valuetext={positionLabel}
      onPointerDown={startDrag}
      onPointerMove={continueDrag}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onKeyDown={seekOnKey}
    >
      {bars.map((height, index) => (
        <div
          key={index}
          className={classes.bar}
          style={{
            height: `${Math.round(height * 100)}%`,
            background: index < playedCount ? PLAYED_COLOUR : IDLE_COLOUR,
          }}
        />
      ))}
    </div>
  );
}
